import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { displayReferralCode } from '../lib/utils';
import {
  Search, X, ChevronDown, ChevronUp, Users, Share2,
  Eye, GitBranch, ArrowRight, RefreshCw, Info
} from 'lucide-react';

interface TrafficSummary {
  clicks: number;
  uniqueVisitors: number;
  pageViews: number;
  addToCarts: number;
  beginCheckouts: number;
  orders: number;
  revenue: number;
  clickToOrderRate: string;
  visitorToOrderRate: string;
}

interface ReferralProfile {
  id: number;
  phone: string;
  name?: string | null;
  referralCode: string;
  totalDirectRefs: number;
  totalIndirectRefs: number;
  totalNetworkSize: number;
  totalOrderValue: number;
  totalRewardEarned?: number;
  tier: string;
  maxDepth?: number;
  createdAt?: number;
  traffic?: TrafficSummary;
}

const TIER_STYLES: Record<string, string> = {
  bronze: 'bg-amber-100 text-amber-700',
  silver: 'bg-gray-100 text-gray-700',
  gold: 'bg-yellow-100 text-yellow-700',
  platinum: 'bg-purple-100 text-purple-700',
};

const TIER_LABELS: Record<string, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

const SORT_OPTIONS = [
  { key: 'totalOrderValue', label: '帶來訂單金額' },
  { key: 'totalDirectRefs', label: '直接推薦數' },
  { key: 'totalIndirectRefs', label: '間接推薦數' },
  { key: 'totalNetworkSize', label: '網絡總人數' },
  { key: 'trafficClicks', label: '連結獨立點擊' },
  { key: 'trafficVisitors', label: '獨立訪客' },
  { key: 'trafficOrders', label: '流量成交單' },
  { key: 'trafficRevenue', label: '流量營收' },
  { key: 'trafficConversionRate', label: '訪客轉化率' },
];

function formatDollars(dollars = 0): string {
  return `HK$${Math.round(dollars).toLocaleString('zh-HK')}`;
}
function formatCents(cents = 0): string {
  return `HK$${Math.round(cents / 100).toLocaleString('zh-HK')}`;
}

const AdminReferrals: React.FC = () => {
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState<ReferralProfile[]>([]);
  const [allProfiles, setAllProfiles] = useState<ReferralProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('');
  const [sortBy, setSortBy] = useState('totalOrderValue');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [trafficDays, setTrafficDays] = useState(30);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
  }, [navigate]);

  // Reset to page 1 when filters / sort / traffic period change
  useEffect(() => {
    setPage(1);
  }, [search, tier, sortBy, sortOrder, trafficDays]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const listParams = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          search,
          tier,
          sortBy,
          sortOrder,
          trafficDays: String(trafficDays),
        });
        const listRes = await apiFetch('/api/public/admin/referrals?' + listParams.toString());
        if (!listRes.ok) {
          const errData = await listRes.json().catch(() => ({}));
          throw new Error(errData.error || '獲取推薦列表失敗');
        }
        const listJson = await listRes.json();
        if (!cancelled) {
          setProfiles(Array.isArray(listJson?.data) ? listJson.data : []);
          setTotal(Number(listJson?.total) || 0);
        }

        const statsParams = new URLSearchParams({
          page: '1',
          limit: '100',
          search,
          tier,
          sortBy: 'totalOrderValue',
          sortOrder: 'desc',
          trafficDays: String(trafficDays),
        });
        const statsRes = await apiFetch('/api/public/admin/referrals?' + statsParams.toString());
        if (statsRes.ok) {
          const statsJson = await statsRes.json().catch(() => ({}));
          if (!cancelled) {
            setAllProfiles(Array.isArray(statsJson?.data) ? statsJson.data : []);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(e.message || '加載失敗');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [page, limit, search, tier, sortBy, sortOrder, trafficDays]);

  const handleSync = async () => {
    if (!confirm('確定要同步所有遺漏的推薦佣金紀錄嗎？')) return;
    setSyncing(true);
    setSyncMessage(null);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/referrals/sync', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSyncMessage(`已同步 ${data.data?.synced || 0} 筆遺漏紀錄`);
        // refresh list
        window.location.reload();
      } else {
        setError(data.error || '同步失敗');
      }
    } catch (e: any) {
      setError(e.message || '同步失敗');
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const totalReferrers = total;
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  const thisMonthCount = allProfiles.filter(
    (p) => (p.createdAt || 0) * 1000 >= startOfMonth.getTime()
  ).length;
  const networkOrderValue = allProfiles.reduce(
    (sum, p) => sum + (p.totalOrderValue || 0),
    0
  );
  const avgDepth = allProfiles.length
    ? allProfiles.reduce((sum, p) => sum + (p.maxDepth || 0), 0) / allProfiles.length
    : 0;

  return (
    <AdminLayout currentPage="referrals" onLogout={handleLogout}>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Share2 className="w-6 h-6 text-red-600" />
            推薦網絡
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            管理客戶推薦關係與網絡表現
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="inline-flex items-center gap-1 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition text-sm font-medium disabled:opacity-50"
        >
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
          {syncing ? '同步中...' : '同步遺漏佣金'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">總推薦人數</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-red-600" />
            {totalReferrers.toLocaleString('zh-HK')}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">本月新增推薦關係</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Share2 className="w-5 h-5 text-green-600" />
            {thisMonthCount.toLocaleString('zh-HK')}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">網絡總訂單金額</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{formatCents(networkOrderValue)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">平均網絡深度</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white">{avgDepth.toFixed(1)}</div>
        </div>
      </div>

      {/* Tier / depth explanation */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">等級自動區分邏輯</p>
            <p className="mb-1">
              等級由該推薦人「帶來的訂單總額」自動計算：
              <span className="font-medium">Bronze</span> &lt; HK$1,000；
              <span className="font-medium">Silver</span> ≥ HK$1,000；
              <span className="font-medium">Gold</span> ≥ HK$3,000；
              <span className="font-medium">Platinum</span> ≥ HK$6,000。
            </p>
            <p>
              目前系統記錄直接推薦關係（treeDepth = 1），鏈路圖的顏色層級僅作視覺區分；
              將來多級網絡會以 <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">path</code> / <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">tree_depth</code> 自動劃分上下線層數。
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="姓名、電話、推薦碼..."
                className="w-full pl-9 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">等級</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
            >
              <option value="">全部等級</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">排序</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">流量區間</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={trafficDays}
              onChange={(e) => setTrafficDays(Number(e.target.value))}
            >
              <option value={7}>最近 7 天</option>
              <option value={30}>最近 30 天</option>
              <option value={90}>最近 90 天</option>
            </select>
          </div>
          <div className="md:col-span-1 flex items-center justify-end gap-3">
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 px-2 py-1 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {sortOrder === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              {sortOrder === 'desc' ? '降序' : '升序'}
            </button>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              共 <span className="font-bold text-gray-800 dark:text-white">{total}</span> 位
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                <th className="p-4">推薦人</th>
                <th className="p-4">推薦碼</th>
                <th className="p-4">等級</th>
                <th className="p-4 text-center">直接推薦</th>
                <th className="p-4 text-center">點擊</th>
                <th className="p-4 text-center">訪客</th>
                <th className="p-4 text-center">成交單</th>
                <th className="p-4 text-right">帶來訂單</th>
                <th className="p-4 text-center">轉化率</th>
                <th className="p-4">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-gray-400">加載中...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-red-500">{error}</td>
                </tr>
              ) : profiles.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    暫無推薦人數據
                  </td>
                </tr>
              ) : (
                profiles.map((p) => (
                  <tr key={p.phone} className="hover:bg-red-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center text-red-600 dark:text-red-400 text-sm font-bold">
                          {p.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-300">{p.name || '匿名'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{p.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm font-mono text-gray-700 dark:text-gray-300">{displayReferralCode(p.referralCode)}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TIER_STYLES[p.tier?.toLowerCase()] || TIER_STYLES.bronze}`}>
                        {TIER_LABELS[p.tier?.toLowerCase()] || p.tier}
                      </span>
                    </td>
                    <td className="p-4 text-center text-sm font-medium text-gray-800 dark:text-white">{p.totalDirectRefs}</td>
                    <td className="p-4 text-center text-sm font-medium text-blue-700 dark:text-blue-400">{p.traffic?.clicks ?? 0}</td>
                    <td className="p-4 text-center text-sm font-medium text-indigo-700 dark:text-indigo-400">{p.traffic?.uniqueVisitors ?? 0}</td>
                    <td className="p-4 text-center text-sm font-medium text-green-700 dark:text-green-400">{p.traffic?.orders ?? 0}</td>
                    <td className="p-4 text-right text-sm font-bold text-gray-800 dark:text-white">{formatCents(p.totalOrderValue ?? 0)}</td>
                    <td className="p-4 text-center text-sm font-medium text-gray-800 dark:text-white">{p.traffic?.visitorToOrderRate ?? '0%'}</td>
                    <td className="p-4 space-x-2 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/admin/referrals/${encodeURIComponent(p.phone)}`)}
                        className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1.5 rounded text-xs hover:bg-red-700 transition-colors"
                      >
                        <Eye size={12} /> 詳情
                      </button>
                      <button
                        onClick={() => navigate(`/admin/referrals/${encodeURIComponent(p.phone)}/tree`)}
                        className="inline-flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-700 transition-colors"
                      >
                        <GitBranch size={12} /> 鏈路圖
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center items-center gap-3">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              上一頁
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              第 {page} / {totalPages} 頁
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              下一頁
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReferrals;
